import React, { useCallback, useEffect, useRef, useState } from "react";
import { DYNAMIC_ISLAND } from "../../../config/dynamicIsland.configuration";
import { getUIColorPalette } from "../../../theme/ColorPalette";

interface DynamicIslandHorizontalScrollProps {

    /** Whether dark UI palette is active. */
    "isDarkUi": boolean;

    /** Pixels scrolled per arrow click. */
    "stepPx"?: number;

    /** Scrollable variant content. */
    "children": React.ReactNode;
}

interface ScrollEdgeState {
    "hasOverflow":    boolean;
    "canScrollLeft":  boolean;
    "canScrollRight": boolean;
}

const SCROLL_EDGE_TOLERANCE_PX = 1;

/** Fixed width reserved for each edge arrow control when visible. */
const ARROW_ZONE_WIDTH_CLASS_NAME = "w-6";

const HIDDEN_SCROLLBAR_CLASS_NAME =
    "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]";

/**
 * Resolves whether horizontal overflow exists on each edge of a scroll container.
 *
 * @param element Scroll container element.
 * @returns Edge scroll availability.
 */
function readHorizontalScrollEdges( element: HTMLDivElement ): ScrollEdgeState {
    const hasOverflow = element.scrollWidth > element.clientWidth + SCROLL_EDGE_TOLERANCE_PX;
    const canScrollRight = element.scrollLeft + element.clientWidth <
      element.scrollWidth - SCROLL_EDGE_TOLERANCE_PX;

    return {
        "hasOverflow":    hasOverflow,
        "canScrollLeft":  element.scrollLeft > SCROLL_EDGE_TOLERANCE_PX,
        "canScrollRight": canScrollRight,
    };
}

/**
 * Compares scroll edge snapshots to avoid redundant state updates.
 *
 * @param previous Previous edge state.
 * @param next Next edge state.
 * @returns True when all edge flags are equal.
 */
function areScrollEdgeStatesEqual( previous: ScrollEdgeState, next: ScrollEdgeState ): boolean {
    return previous.hasOverflow === next.hasOverflow &&
      previous.canScrollLeft === next.canScrollLeft &&
      previous.canScrollRight === next.canScrollRight;
}

interface DynamicIslandHorizontalScrollArrowProps {
    "ariaLabel": string;
    "direction": "left" | "right";
    "onClick":   () => void;
    "stroke":    string;
}

/**
 * Renders a dedicated edge arrow button inside its own fixed-width column.
 *
 * @param props Arrow button props.
 * @returns Edge stepper button.
 */
const DynamicIslandHorizontalScrollArrow: React.FC<DynamicIslandHorizontalScrollArrowProps> = ( {
    ariaLabel,
    direction,
    onClick,
    stroke,
} ) => (
    <button
        aria-label={ariaLabel}
        className={`pointer-events-auto flex h-full ${ ARROW_ZONE_WIDTH_CLASS_NAME } shrink-0 items-center justify-center`}
        onClick={onClick}
        type="button"
    >
        <svg
            aria-hidden={true}
            className="h-3.5 w-3.5"
            fill="none"
            stroke={stroke}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            viewBox="0 0 24 24"
        >
            <path d={direction === "left" ? "M15 18l-6-6 6-6" : "M9 18l6-6-6-6"} />
        </svg>
    </button>
);

/**
 * Generic horizontal scroll layer: hidden scrollbar, native scroll preserved,
 * gray edge arrows in dedicated side columns that advance one step per click.
 *
 * @param props Component props.
 * @returns Scroll row with optional edge stepper columns.
 */
const DynamicIslandHorizontalScroll: React.FC<DynamicIslandHorizontalScrollProps> = ( {
    isDarkUi,
    stepPx,
    children,
} ) => {
    const palette = getUIColorPalette( isDarkUi );
    const scrollRef = useRef<HTMLDivElement>( null );
    const [ scrollEdges, setScrollEdges ] = useState<ScrollEdgeState>( {
        "hasOverflow":    false,
        "canScrollLeft":  false,
        "canScrollRight": false,
    } );

    const refreshScrollEdges = useCallback( (): void => {
        const element = scrollRef.current;

        if ( element === null ) {
            return;
        }

        setScrollEdges( ( previous ) => {
            const next = readHorizontalScrollEdges( element );

            return areScrollEdgeStatesEqual( previous, next ) ? previous : next;
        } );
    }, [] );

    useEffect( () => {
        const element = scrollRef.current;

        if ( element === null ) {
            return;
        }

        refreshScrollEdges();

        const resizeObserver = new ResizeObserver( () => {
            window.requestAnimationFrame( () => {
                refreshScrollEdges();
            } );
        } );

        resizeObserver.observe( element );

        if ( element.firstElementChild instanceof HTMLElement ) {
            resizeObserver.observe( element.firstElementChild );
        }

        element.addEventListener( "scroll", refreshScrollEdges, { "passive": true } );

        return () => {
            resizeObserver.disconnect();
            element.removeEventListener( "scroll", refreshScrollEdges );
        };

        /*
         * Content size changes are observed via ResizeObserver on the scroll container
         * and its first child; do not depend on `children` (new reference every render).
         */
    }, [ refreshScrollEdges ] );

    const scrollByStep = useCallback( ( direction: "left" | "right" ): void => {
        const element = scrollRef.current;

        if ( element === null ) {
            return;
        }

        const resolvedStepPx = stepPx ??
          DYNAMIC_ISLAND.horizontalScrollStepPx ??
          Math.max( 80, Math.round( element.clientWidth * 0.5 ) );
        const delta = direction === "left" ? -resolvedStepPx : resolvedStepPx;

        element.scrollBy( {
            "left":     delta,
            "behavior": "smooth",
        } );
    }, [ stepPx ] );

    const handleScrollLeft = useCallback( (): void => {
        scrollByStep( "left" );
    }, [ scrollByStep ] );

    const handleScrollRight = useCallback( (): void => {
        scrollByStep( "right" );
    }, [ scrollByStep ] );

    const arrowColor = palette.overlayTextSecondary;

    return (
        <div className="flex h-full w-full min-w-0 items-stretch">
            {scrollEdges.canScrollLeft
                ? (
                    <DynamicIslandHorizontalScrollArrow
                        ariaLabel="Scroll itinerary summary left"
                        direction="left"
                        onClick={handleScrollLeft}
                        stroke={arrowColor}
                    />
                )
                : null}
            <div
                className={`h-full min-w-0 flex-1 overflow-x-auto overflow-y-hidden touch-pan-x ${ HIDDEN_SCROLLBAR_CLASS_NAME }`}
                ref={scrollRef}
            >
                {children}
            </div>
            {scrollEdges.canScrollRight
                ? (
                    <DynamicIslandHorizontalScrollArrow
                        ariaLabel="Scroll itinerary summary right"
                        direction="right"
                        onClick={handleScrollRight}
                        stroke={arrowColor}
                    />
                )
                : null}
        </div>
    );
};

export default DynamicIslandHorizontalScroll;
